/**
 * Preference question input UI by question type.
 *
 * Belongs here: question rendering.
 * Does not belong here: flow rules.
 */
import { ExpertReviewScreen } from '../../../analytics/expertReviewEvents'
import type { Question } from '../../../shared/types/questionnaire.types'
import { QuestionCard } from '../../../shared/components/QuestionCard'
import { PreferenceInsightQuestion } from './PreferenceInsightQuestion'
import {
  MultiSelectGroupsQuestion,
  getSelectedOptionIds,
  toggleSelectedOptionId
} from './MultiSelectGroupsQuestion'
import type { PreferencesAnswersMap } from '../../setup/types'
import { isSegmentIntroQuestion } from '../../../question-utils/preferencesMappers'

interface PreferencesQuestionBlockProps {
  question: Question
  answers: PreferencesAnswersMap
  onAdvance: (questionId: string, value: string | number) => void
  /** UI selection / multi-select only — does not move forward. */
  updateDraftAnswer: (questionId: string, value: string | number | string[]) => void
  onAmountClear?: () => void
  amountHelperText?: string
  contextId?: string
}

/** Routes preference question types to the right UI. No navigation. */
export function PreferencesQuestionBlock({
  question,
  answers,
  onAdvance,
  updateDraftAnswer,
  onAmountClear,
  amountHelperText,
  contextId
}: PreferencesQuestionBlockProps) {
  const inputType = question.input?.type
  if (isSegmentIntroQuestion(question)) {
    return (
      <PreferenceInsightQuestion
        question={question}
        analyticsScreen={ExpertReviewScreen.PreferencesQuestionnaire}
        contextId={contextId}
      />
    )
  }
  if (inputType === 'MULTI_SELECT_GROUPS') {
    const selectedOptionIds = getSelectedOptionIds(answers[question.id])
    return (
      <MultiSelectGroupsQuestion
        question={question}
        selectedOptionIds={selectedOptionIds}
        analyticsScreen={ExpertReviewScreen.PreferencesQuestionnaire}
        contextId={contextId}
        onToggleOption={(optionId) =>
          updateDraftAnswer(question.id, toggleSelectedOptionId(selectedOptionIds, optionId))
        }
      />
    )
  }
  return (
    <QuestionCard
      question={question}
      answers={answers}
      onAdvance={onAdvance}
      updateDraftAnswer={(questionId, value) => {
        updateDraftAnswer(questionId, value)
      }}
      onAmountClear={onAmountClear}
      amountHelperText={amountHelperText}
      contextId={contextId}
      analyticsScreen={ExpertReviewScreen.PreferencesQuestionnaire}
    />
  )
}
